import { t } from "@i18n";
import { presetSectionsCodeObject, SECTION_TYPE_TABLE_SECTION, formCategories } from "@Helpers";
import { getPresetSectionsObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";

/**
 * Coerce a raw form-field value to a finite number, or `null` when the value is
 * blank/undefined/non-numeric. Used to build a numeric scope for formula
 * evaluation so a referenced-but-empty field leaves the formula incomplete
 * (evaluates to `null`) instead of being silently treated as `0`.
 */
export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Join the resolved values of `selectedFields` (in order) using `seperator`.
 * Falsy values are skipped; returns "" when nothing resolves.
 */
export const buildConcatenatedValue = (
  selectedFields: Array<string> | undefined,
  valueById: Map<string, string>,
  seperator: string | undefined,
): string =>
  (selectedFields ?? [])
    .map((id) => valueById.get(id))
    .filter(Boolean)
    .join(seperator ?? "");

export const TEMPLATE_SOURCE_MAIL_CREATION = "MAIL_CREATION";
export const TEMPLATE_SOURCE_MAIL_SIDEBAR = "MAIL_SIDEBAR";

export const preSubmissionTypeDataSource: any = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
];
export const postSubmissionTypeDataSource: any = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
  { name: "Verify", value: "Verify" },
];
export const linkBoqSources: any = {
  INSPECTION_FORM: "inspectionForms",
  LINK_ITEMS: "linkItems",
};
export const formStatuses: any = {
  inProgress: "IN_PROGRESS",
  rejected: "REJECTED",
  approved: "APPROVED",
  submitted: "SUBMITTED",
};
export const formStatusFilterDataSource: any = [
  { name: "In Progress", value: formStatuses.inProgress },
  { name: "Rejected", value: formStatuses.rejected },
  { name: "Approved", value: formStatuses.approved },
  { name: "Submitted", value: formStatuses.submitted },
];

export const formSectionType: any = {
  FORM: "form",
  TABLE_SECTION: "tableSection",
};
export const getUserPermissionForForm = (inspectionData: any, userId: any, sectionIndex: any) => {
  const isCurrentUserAllowed =
    (inspectionData.selectedForm.template[sectionIndex].permissions[inspectionData.currentLevelOfForm].Fill.includes(
      userId,
    ) ||
      inspectionData.selectedForm.template[sectionIndex].permissions[inspectionData.currentLevelOfForm].Modify.includes(
        userId,
      )) &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived.status;

  return isCurrentUserAllowed;
};
export const getFillPermissionForForm = (inspectionData: any, userId: any, sectionIndex: any) => {
  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived.status &&
    inspectionData.selectedForm.template[sectionIndex].permissions[inspectionData.currentLevelOfForm].Fill.includes(
      userId,
    );

  return isCurrentUserAllowed;
};
export const getModifyPermissionForForm = (inspectionData: any, userId: any, sectionIndex: any, modifySection: any) => {
  const sectionId = inspectionData.selectedForm?.template[sectionIndex]?._id;

  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived.status &&
    inspectionData.selectedForm.template[sectionIndex].permissions[inspectionData.currentLevelOfForm].Modify.includes(
      userId,
    ) &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived.status &&
    modifySection?.includes(sectionId);

  return isCurrentUserAllowed;
};

export const getSectionPermission = ({
  section,
  inspectionData,
  userId,
  type,
}: {
  section: any;
  inspectionData: any;
  // Current user's id; may be absent before the session loads — `.includes(undefined)` then
  // safely yields no-permission.
  userId: string | undefined;
  type: string;
}) => {
  const isCurrentUserAllowed =
    section.permissions[inspectionData.currentLevelOfForm][type].includes(userId) &&
    !inspectionData.isArchived.status &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved;

  return isCurrentUserAllowed;
};
export const getPermissions = ({
  inspectionData,
  permissions = {},
  type,
  userId,
}: {
  inspectionData: any;
  permissions: any;
  type: string;
  userId: string;
}) => {
  if (typeof permissions == "object" && !Object.keys(permissions).length) {
    return true;
  }
  if (inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status) {
    return false;
  }
  return !!permissions?.[inspectionData.currentLevelOfForm]?.[type]?.includes(userId);
};

export const getUrlFromField = (url: any) => {
  const newUrl = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
  return newUrl;
};

const getTextRules = (field: any) => {
  return {
    minLength: field.minFieldLimit ?? 0,
    ...(field.maxFieldLimit && { maxLength: field.maxFieldLimit }),
  };
};

const getNumberRules = (field: any) => {
  return {
    minRange: field.minFieldRange ?? 0,
    ...(field.maxFieldRange && { maxRange: field.maxFieldRange }),
  };
};

const getFileRules = (field: any) => {
  return {
    ...(field.maxNoOfFiles && { maxNoOfFiles: field.maxNoOfFiles }),
    ...(field.minNoOfFiles && { minNoOfFiles: field.minNoOfFiles }),
  };
};

const getDateTimeRules = (field: any) => {
  const rules: any = {
    ...(field.maxFieldRange && { maxFieldRange: field.maxFieldRange }),
    ...(field.minFieldRange && { minFieldRange: field.minFieldRange }),
  };
  if (["futureDateTime", "pastDateTime"].includes(field.selectionScope)) {
    rules.selectionScope = field.selectionScope;
  }
  return rules;
};

const generateRuleObj = (eachField: any, row?: any) => {
  const value = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

  let ruleObj: any = {};

  if (eachField.isMandatory) {
    ruleObj.required = true;
  }

  switch (eachField.fieldType) {
    case "single-line":
    case "multi-line":
      ruleObj = { ...ruleObj, ...getTextRules(eachField) };
      break;
    case "number":
      ruleObj = { ...ruleObj, ...getNumberRules(eachField) };
      break;
    case "fileUpload":
    case "image":
      ruleObj = { ...ruleObj, ...getFileRules(eachField) };
      break;
    case "date-time":
      ruleObj = { ...ruleObj, ...getDateTimeRules(eachField) };
      break;
  }

  return { ...ruleObj, value };
};

export function addValidationToForms({
  formValidator,
  template = [],
  inspectionData,
  rows,
  modifySection = [],
  templateSource,
  resourceType,
}: {
  formValidator: any;
  template?: any;
  inspectionData?: any;
  rows?: any;
  modifySection?: any;
  templateSource?: string;
  resourceType?: string;
}) {
  const isMailSidebarContext =
    resourceType === formCategories.mailBox && templateSource === TEMPLATE_SOURCE_MAIL_SIDEBAR;

  const allSectionsInCurrentLevel: any = [];
  if (inspectionData?._id) {
    inspectionData.formFlow[inspectionData.currentLevelOfForm].dataSource.map((data: any) => {
      if (data.type === "Fill") {
        data.sections.map((section: any) => allSectionsInCurrentLevel.push(section._id));
      } else if (data.type === "Modify") {
        data.sections.map((section: any) => {
          if (modifySection.includes(section._id)) {
            allSectionsInCurrentLevel.push(section._id);
          }
        });
      }
    });
    template = inspectionData.selectedForm.template;
  } else if (template?.length) {
    template?.forEach((section: any) => {
      allSectionsInCurrentLevel.push(section._id);
    });
  }
  const options: any = { rules: {} };
  template?.forEach((section: any) => {
    if (
      section.sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION &&
      allSectionsInCurrentLevel.includes(section._id)
    ) {
      section.questionFields.forEach((eachField: any) => {
        section.tableDataSource.forEach((row: any) => {
          const ruleName: string = `${row.rowId}${eachField._id}`;
          const rulesObj: any = generateRuleObj(eachField, row);
          if (isMailSidebarContext && !eachField.isMetadata) {
            delete rulesObj.required;
          }
          options.rules[ruleName] = {
            ...rulesObj,
          };
        });
      });
    } else if (section.questionFields && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.forEach((eachField: any) => {
        const ruleName = `${eachField._id}`;
        const rulesObj: any = generateRuleObj(eachField);
        if (isMailSidebarContext && !eachField.isMetadata) {
          delete rulesObj.required;
        }
        if (rows) {
          rows.forEach((row: any) => {
            options.rules[`${row.id}_${ruleName}`] = {
              ...rulesObj,
            };
          });
        } else {
          options.rules[ruleName] = {
            ...rulesObj,
          };
        }
      });
    }
  });
  formValidator.updateRules(options.rules);
}

export function checkMandatoryFields(inspectionData: any) {
  const allSectionsInCurrentLevel: any = [];
  inspectionData.formFlow[inspectionData.currentLevelOfForm].dataSource.map((data: any) => {
    data.sections.map((section: any) => allSectionsInCurrentLevel.push(section._id));
  });
  let errorToastContent = t("Form submission failed: Not all mandatory fields filled.");
  let isAllMandatoryFieldsFilled = true;

  const isEmptyValue = (value: any) => {
    if (["", undefined, null].includes(value)) return true;
    if (Array.isArray(value)) return value.join("").trim() === "";
    if (typeof value === "object" && Object.keys(value).length === 0) return true;
    return false;
  };

  const validateMandatory = (isMandatory: boolean, value: any) => {
    if (isMandatory && isEmptyValue(value)) {
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateTextField = (field: any, value: any) => {
    if (!["single-line", "multi-line"].includes(field.fieldType) || !value?.length) return false;

    if (field.minFieldLimit && field.minFieldLimit > value.length) {
      errorToastContent = t(`Form submission failed: Minimum characters criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    if (field.maxFieldLimit && value.length > field.maxFieldLimit) {
      errorToastContent = t(`Form submission failed: Entered value exceeds the max characters set for this field`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateDateTime = (field: any, value: any) => {
    if (field.fieldType !== "date-time") return false;

    if (field.selectionScope === "pastDateTime" && value && value > Date.now()) {
      errorToastContent = t("Form submission failed: Please select a date and time from the past");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (
      field.selectionScope === "rangeBased" &&
      ((field.minFieldRange && value && value < field.minFieldRange) ||
        (field.maxFieldRange && value && value > field.maxFieldRange))
    ) {
      errorToastContent = t("Form submission failed: Please select a date and time within the predefined range");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    return false;
  };

  const validateFiles = (field: any, value: any) => {
    if (!Array.isArray(value) || !value.length) return false;

    if (field.minNoOfFiles && field.minNoOfFiles > value.length) {
      errorToastContent = t(`Form submission failed: Minimun files criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (field.maxNoOfFiles && value.length > field.maxNoOfFiles) {
      isAllMandatoryFieldsFilled = false;
      errorToastContent = t("Form submission failed: Files uploaded exceeds the max limit set for this field");
      return true;
    }

    return false;
  };

  const generateRule = (eachField: any, row?: any) => {
    const answerValue = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

    return (
      validateMandatory(eachField.isMandatory, answerValue) ||
      validateTextField(eachField, answerValue) ||
      validateDateTime(eachField, answerValue) ||
      validateFiles(eachField, answerValue)
    );
  };

  inspectionData.selectedForm.template?.some((section: any) => {
    if (
      section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION &&
      allSectionsInCurrentLevel.includes(section._id)
    ) {
      section.questionFields.some((eachField: any) => {
        if (section?.tableDataSource?.length) {
          section.tableDataSource.some((row: any) => {
            return generateRule(eachField, row);
          });
        } else {
          return generateRule(eachField);
        }
      });
    } else if (section.questionFields && !section.sectionCode && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.some((eachField: any) => {
        return generateRule(eachField);
      });
      return !isAllMandatoryFieldsFilled;
    }
    return false;
  });
  return {
    errorToastContent,
    isAllMandatoryFieldsFilled,
  };
}
export const formStatusMapper: any = {
  [formStatuses.inProgress]: "In Progress",
  [formStatuses.rejected]: "Rejected",
  [formStatuses.approved]: "Approved",
  [formStatuses.submitted]: "Submitted",
};
export function getSectionName(section: any) {
  const presetSectionsObject = getPresetSectionsObject();

  if (
    section.sectionCode &&
    section.sectionCode != SECTION_TYPE_TABLE_SECTION &&
    presetSectionsObject[section.sectionCode]
  ) {
    return presetSectionsObject[section.sectionCode];
  }
  return section.sectionHeading;
}
